import type { AccountAiCreditsResponse } from 'definitions/editor-ai-ui';
import type { MeterView } from 'helpers/ai-credits';
import { formatCredits, formatResetDate, meterView } from 'helpers/ai-credits';

export type AiCreditMeter = {
  /** The track, its level and the words: read exactly as the editor's and the results chat's meters. */
  view: MeterView;
  /** "Includes 500 credits from credit packs", or null without packs (never for a capped account). */
  packs: string | null;
  /** "Resets on 1 November", or null when the window has no reset. */
  resets: string | null;
  /** On credits, where Billing sells packs. A capped account (invoice, free, internal) has nothing to buy there. */
  onCredits: boolean;
};

/** The server's answer, or nothing while it loads. */
type MeterResponse = Readonly<AccountAiCreditsResponse> | null | undefined;

/**
 * What the header's AI meter shows for GET /billing/ai-credits, worded as the editor's top bar
 * meter is: the credits left, any packs among them, when it resets, and the way to buy more only
 * where there is something to buy. Null while loading and for a caller with no account.
 */
export const aiCreditMeter = (response: MeterResponse): AiCreditMeter | null => {
  const credits = response?.credits;
  if (!response || !credits) return null;
  const resets = formatResetDate(credits.resetsAt);
  return {
    view: meterView(credits),
    packs: credits.packRemaining > 0 ? `Includes ${formatCredits(credits.packRemaining)} from credit packs` : null,
    resets: resets ? `Resets on ${resets}` : null,
    onCredits: response.billingUrl !== null,
  };
};

/**
 * What the header's pill is showing. It is always on screen (decided 2026-10-20), so the states
 * without figures have a pill of their own instead of nothing: loading, a read that failed, and a
 * caller with no company, who has no account to measure.
 */
export type AiCreditMeterState =
  | { status: 'ready'; meter: AiCreditMeter }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'none' };

type AiCreditMeterStateParams = { response: MeterResponse; isError: boolean };

/** Figures already read win over a failed refetch: the last balance is still the best one known. */
export const aiCreditMeterState = ({ response, isError }: AiCreditMeterStateParams): AiCreditMeterState => {
  const meter = aiCreditMeter(response);
  if (meter) return { status: 'ready', meter };
  if (isError) return { status: 'error' };
  if (response === undefined || response === null) return { status: 'loading' };
  return { status: 'none' };
};
